import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteTireConditionMutation,
  usePostTireConditionSetActiveMutation,
  usePostTireConditionSetDefaultMutation,
  usePostTireConditionSetInactiveMutation,
  usePostTireConditionUnsetDefaultMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function TireCondition() {
  const navigate = useNavigate();

  const [deleteTireConditionApi] = useDeleteTireConditionMutation();
  const [postTireConditionSetActiveApi] = usePostTireConditionSetActiveMutation();
  const [postTireConditionSetInactiveApi] = usePostTireConditionSetInactiveMutation();
  const [postTireConditionSetDefaultApi] = usePostTireConditionSetDefaultMutation();
  const [postTireConditionUnsetDefaultApi] = usePostTireConditionUnsetDefaultMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        navigate(composePath(settingsRoutes.tireConditionDetail, {params: {id}}))
      )
      .with('setInactive', () =>
        postTireConditionSetInactiveApi({tireConditionId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setActive', () =>
        postTireConditionSetActiveApi({tireConditionId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setDefault', () =>
        postTireConditionSetDefaultApi({tireConditionId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteTireConditionApi({tireConditionId: id})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('unsetDefault', () =>
        postTireConditionUnsetDefaultApi({tireConditionId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={i18n.t('entity.tiresInventory.labels.tireCondition')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.tiresInventory.labels.tireCondition'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.tiresInventory.labels.newTireCondition'),
              onClick: () => navigate(settingsRoutes.tireConditionCreate),
            },
          ],
        }}
        data-testid={testIds.settings.tireCondition('template')}
      >
        <DmsDataGrid
          gridCode="tire-condition"
          actionCallback={actionCallback}
          data-testid={testIds.settings.tireCondition('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
